import React, { Suspense, lazy } from 'react';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { Layout } from './components/Layout';
import { DefaultSEO } from './components/SEO/DefaultSEO';

// Lazy load all pages
const HomePage = lazy(() => import('./pages/HomePage').then(m => ({ default: m.HomePage })));
const AboutPage = lazy(() => import('./pages/AboutPage').then(m => ({ default: m.AboutPage })));
const BlogPage = lazy(() => import('./pages/BlogPage').then(m => ({ default: m.BlogPage })));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage').then(m => ({ default: m.BlogPostPage })));
const VolumeBoosterPage = lazy(() => import('./pages/VolumeBoosterPage').then(m => ({ default: m.VolumeBoosterPage })));
const SpeakerFrequencyTestPage = lazy(() => import('./pages/SpeakerFrequencyTestPage').then(m => ({ default: m.SpeakerFrequencyTestPage })));
const MicrophoneTesterPage = lazy(() => import('./pages/MicrophoneTesterPage').then(m => ({ default: m.MicrophoneTesterPage })));
const HeadphonesTestPage = lazy(() => import('./pages/HeadphonesTestPage').then(m => ({ default: m.HeadphonesTestPage })));
const NoiseGeneratorPage = lazy(() => import('./pages/NoiseGeneratorPage').then(m => ({ default: m.NoiseGeneratorPage })));
const BrownNoiseGeneratorPage = lazy(() => import('./pages/BrownNoiseGeneratorPage').then(m => ({ default: m.BrownNoiseGeneratorPage })));
const EqualizerPage = lazy(() => import('./pages/EqualizerPage').then(m => ({ default: m.EqualizerPage })));
const VoiceAssessmentPage = lazy(() => import('./pages/VoiceAssessmentPage').then(m => ({ default: m.VoiceAssessmentPage })));
const VoiceChangerPage = lazy(() => import('./pages/VoiceChangerPage').then(m => ({ default: m.VoiceChangerPage })));
const ContactPage = lazy(() => import('./pages/ContactPage').then(m => ({ default: m.ContactPage })));
const LeftRightStereoSoundTestPage = lazy(() => import('./pages/LeftRightStereoSoundTestPage').then(m => ({ default: m.LeftRightStereoSoundTestPage })));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })));
const TermsOfService = lazy(() => import('./pages/TermsOfService').then(m => ({ default: m.TermsOfService })));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy').then(m => ({ default: m.CookiePolicy })));
const AudioToTextPage = lazy(() => import('./pages/AudioToTextPage').then(m => ({ default: m.AudioToTextPage })));
const OnlineVoiceRecorderPage = lazy(() => import('./pages/OnlineVoiceRecorderPage').then(m => ({ default: m.OnlineVoiceRecorderPage })));
const AmbientNoiseCancellationTestPage = lazy(() => import('./pages/AmbientNoiseCancellationTestPage').then(m => ({ default: m.AmbientNoiseCancellationTestPage })));
const DecibelMeterPage = lazy(() => import('./pages/DecibelMeterPage').then(m => ({ default: m.DecibelMeterPage })));
const NoiseDosimeterPage = lazy(() => import('./pages/NoiseDosimeterPage').then(m => ({ default: m.NoiseDosimeterPage })));
const AudioFormatConverterPage = lazy(() => import('./pages/AudioFormatConverterPage').then(m => ({ default: m.AudioFormatConverterPage })));

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="w-12 h-12 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin" />
  </div>
);

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <>
        <DefaultSEO />
        <Layout />
      </>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageLoader />}>
            <HomePage />
          </Suspense>
        ),
      },
      {
        path: 'about',
        element: (
          <Suspense fallback={<PageLoader />}>
            <AboutPage />
          </Suspense>
        ),
      },
      {
        path: 'blog',
        element: (
          <Suspense fallback={<PageLoader />}>
            <BlogPage />
          </Suspense>
        ),
      },
      {
        path: 'blog/:id',
        element: (
          <Suspense fallback={<PageLoader />}>
            <BlogPostPage />
          </Suspense>
        ),
      },
      {
        path: 'mp3-volume-booster',
        element: (
          <Suspense fallback={<PageLoader />}>
            <VolumeBoosterPage />
          </Suspense>
        ),
      },
      {
        path: 'speaker-frequency-test',
        element: (
          <Suspense fallback={<PageLoader />}>
            <SpeakerFrequencyTestPage />
          </Suspense>
        ),
      },
      {
        path: 'microphone-tester',
        element: (
          <Suspense fallback={<PageLoader />}>
            <MicrophoneTesterPage />
          </Suspense>
        ),
      },
      {
        path: 'headphones-earphones-test',
        element: (
          <Suspense fallback={<PageLoader />}>
            <HeadphonesTestPage />
          </Suspense>
        ),
      },
      {
        path: 'white-noise-and-pink-noise-generators',
        element: (
          <Suspense fallback={<PageLoader />}>
            <NoiseGeneratorPage />
          </Suspense>
        ),
      },
      {
        path: 'brown-noise-generator',
        element: (
          <Suspense fallback={<PageLoader />}>
            <BrownNoiseGeneratorPage />
          </Suspense>
        ),
      },
      {
        path: 'equalizer-settings-online',
        element: (
          <Suspense fallback={<PageLoader />}>
            <EqualizerPage />
          </Suspense>
        ),
      },
      {
        path: 'feedback-analyzer',
        element: (
          <Suspense fallback={<PageLoader />}>
            <VoiceAssessmentPage />
          </Suspense>
        ),
      },
      {
        path: 'voice-changer',
        element: (
          <Suspense fallback={<PageLoader />}>
            <VoiceChangerPage />
          </Suspense>
        ),
      },
      {
        path: 'left-right-stereo-sound-test',
        element: (
          <Suspense fallback={<PageLoader />}>
            <LeftRightStereoSoundTestPage />
          </Suspense>
        ),
      },
      {
        path: 'contact',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ContactPage />
          </Suspense>
        ),
      },
      {
        path: 'privacy',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PrivacyPolicy />
          </Suspense>
        ),
      },
      {
        path: 'terms',
        element: (
          <Suspense fallback={<PageLoader />}>
            <TermsOfService />
          </Suspense>
        ),
      },
      {
        path: 'cookies',
        element: (
          <Suspense fallback={<PageLoader />}>
            <CookiePolicy />
          </Suspense>
        ),
      },
      {
        path: 'audio-to-text',
        element: (
          <Suspense fallback={<PageLoader />}>
            <AudioToTextPage />
          </Suspense>
        ),
      },
      {
        path: 'online-voice-recorder',
        element: (
          <Suspense fallback={<PageLoader />}>
            <OnlineVoiceRecorderPage />
          </Suspense>
        ),
      },
      {
        path: 'ambient-noise-cancellation-test',
        element: (
          <Suspense fallback={<PageLoader />}>
            <AmbientNoiseCancellationTestPage />
          </Suspense>
        ),
      },
      {
        path: 'decibel-meter',
        element: (
          <Suspense fallback={<PageLoader />}>
            <DecibelMeterPage />
          </Suspense>
        ),
      },
      {
        path: 'noise-dosimeter',
        element: (
          <Suspense fallback={<PageLoader />}>
            <NoiseDosimeterPage />
          </Suspense>
        ),
      },
      {
        path: 'audio-format-converter',
        element: (
          <Suspense fallback={<PageLoader />}>
            <AudioFormatConverterPage />
          </Suspense>
        ),
      },
      {
        path: '*',
        element: (
          <div className="min-h-[60vh] flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-white mb-4">404</h1>
              <p className="text-gray-400">Page not found</p>
            </div>
          </div>
        ),
      },
    ],
  },
]);

function App() {
  return <RouterProvider router={router} />;
}

export default App;